// Lightweight client for Shopify Admin plan status
import { authFetch } from '../shopify/authFetch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

// Strict plan mapping by name/handle
const PLAN_MAPPING = {
  "Trial": "trial", "trial": "trial",
  "Starter": "starter", "starter": "starter",
  "Growth": "growth", "growth": "growth",
  "Enterprise": "enterprise", "enterprise": "enterprise",
};

// Plan metadata for display only
const PLAN_META = {
  "trial": { "price": 0.00, "currency": "USD", "limit": 999999 },
  "starter": { "price": 19.99, "currency": "USD", "limit": 750 },
  "growth": { "price": 49.99, "currency": "USD", "limit": 2500 },
  "enterprise": { "price": 149.99, "currency": "USD", "limit": 10000 },
};

/**
 * Add nonce to URL for cache busting
 */
export function withNonce(url) {
  const u = new URL(url, window.location.origin);
  u.searchParams.set('_', String(Date.now()));
  return u.toString();
}

export async function fetchLivePlan(shop, { apiVersion = '2025-07', bustCache = true } = {}) {
  if (!shop) throw new Error('shop is required');
  
  // Add cache buster to prevent stale data
  const cacheBuster = bustCache ? `&_t=${Date.now()}` : '';
  const url = `${BACKEND_URL}/api/store/live-plan?shop=${encodeURIComponent(shop)}&api_version=${encodeURIComponent(apiVersion)}${cacheBuster}`;
  
  const res = await authFetch(url, { 
    method: 'GET',
    headers: { 
      'x-api-key': API_KEY,
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  });
  const data = await res.json();
  if (!res.ok) {
    const msg = typeof data?.detail === 'string' ? data.detail : 'Failed to load live plan';
    throw new Error(msg);
  }

  // Normalize a compact shape for display
  const pricing = data?.pricing || null;
  let interval = null;
  let amount = null;
  let currency = null;

  if (pricing) {
    if (pricing.__typename === 'AppRecurringPricing') {
      interval = pricing.interval || null;
      amount = pricing.price?.amount ?? null;
      currency = pricing.price?.currencyCode ?? null;
    } else if (pricing.__typename === 'AppUsagePricing') {
      // Show usage pricing as cappedAmount with terms
      amount = pricing.cappedAmount?.amount ?? null;
      currency = pricing.cappedAmount?.currencyCode ?? null;
      interval = 'usage';
    }
  }

  return {
    success: !!data?.success,
    shop: data?.shop,
    active: !!data?.active,
    name: data?.plan_name || null,
    status: data?.status || null,
    test: !!data?.test,
    created_at: data?.created_at || null,
    trial_days: data?.trial_days ?? null,
    pricing: {
      typename: pricing?.__typename || null,
      interval,
      amount,
      currency,
      raw: pricing || null,
    },
    raw: data?.raw || null,
  };
}

/**
 * Fetch usage and plan data from the enhanced backend usage endpoint
 * This provides complete plan information with live Shopify data and usage stats
 */
export async function fetchUsageAndPlan(shop, { bustCache = false, forcePlanRefresh = false, billingReturn = false } = {}) {
  if (!shop) throw new Error('shop is required');
  
  // Determine URL and cache strategy based on context
  let url = `${BACKEND_URL}/api/usage/current?shop=${encodeURIComponent(shop)}`;
  let fetchOptions = { 
    method: 'GET',
    headers: { 
      'x-api-key': API_KEY
    }
  };
  
  // For billing returns or forced refresh, use aggressive cache busting AND backend force_refresh
  if (billingReturn || forcePlanRefresh || bustCache) {
    // Add force_refresh parameter to trigger backend Shopify GraphQL fetch
    url += `&force_refresh=true`;
    url = withNonce(url);
    fetchOptions.cache = 'no-store';
    fetchOptions.headers['Cache-Control'] = 'no-store';
    fetchOptions.headers['Pragma'] = 'no-cache';
  }
  
  const res = await authFetch(url, fetchOptions);
  const data = await res.json();
  if (!res.ok) {
    const msg = typeof data?.detail === 'string' ? data.detail : 'Failed to load usage and plan data';
    throw new Error(msg);
  }

  return {
    success: true,
    shop: data.shop,
    plan: {
      name: data.plan?.name || 'Unknown Plan',
      handle: data.plan?.handle || 'unknown',
      period: data.plan?.period || 'monthly',
      limit: data.plan?.limit || 0,
      pricing: data.plan?.pricing || {},
      subscription: data.plan?.subscription || {}
    },
    effective_status: data.effective_status || 'inactive',  // Single source of truth for UI
    usage: {
      used: data.usage?.used || 0,
      remaining: data.usage?.remaining || 0,
      currency: data.usage?.currency || 'USD',
      period_start: data.usage?.period_start_utc || null
    },
    trial: data.trial || null,  // Trial data for completeness  
    gating: data.gating || {},
    ui_today: data.ui_today || {},
    recent: data.recent || [],
    data_source: data.data_source || 'unknown'
  };
}